// =============================================================================
// Tiny Web Audio sound helper for the arcade — no asset files, all synthesized.
// Shared by every game (clicks, scores, win/lose jingles) and Simon (the colored
// pad tones). Respects a global mute persisted in localStorage.
// =============================================================================

const MUTE_KEY = 'akt_games_muted';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctx) ctx = new Ctor();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

export function isMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setMuted(muted: boolean): void {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event('akt_games_changed')); } catch { /* ignore */ }
}

export function toggleMuted(): boolean {
  const next = !isMuted();
  setMuted(next);
  return next;
}

/** Play a single note. Exposed for Simon (distinct pad tones). */
export function tone(freq: number, durationMs = 180, type: OscillatorType = 'sine', gain = 0.18): void {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(gain, now + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.02);
}

function sequence(notes: { f: number; t: number; d?: number; type?: OscillatorType }[]) {
  if (isMuted()) return;
  notes.forEach(n => setTimeout(() => tone(n.f, n.d ?? 150, n.type ?? 'sine'), n.t));
}

export const sfx = {
  click: () => tone(440, 60, 'triangle', 0.12),
  move: () => tone(330, 50, 'square', 0.08),
  place: () => tone(523, 90, 'triangle', 0.12),
  score: () => tone(784, 120, 'sine', 0.16),
  pop: () => tone(660, 80, 'sine', 0.14),
  // Cheerful rising arpeggio.
  win: () => sequence([{ f: 523, t: 0 }, { f: 659, t: 110 }, { f: 784, t: 220 }, { f: 1047, t: 330, d: 260 }]),
  // Descending sad blip.
  lose: () => sequence([{ f: 392, t: 0, type: 'sawtooth' }, { f: 294, t: 120, type: 'sawtooth' }, { f: 196, t: 240, d: 280, type: 'sawtooth' }]),
  levelUp: () => sequence([{ f: 659, t: 0 }, { f: 988, t: 120, d: 240 }]),
};
